import { useCallback, useMemo, useRef, useState } from "react";
import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react";
import { GLASelectorMenu, GLASelectorPill, GLASelectorTrigger } from "./components";
import type { GlAccount } from "./types";
import { GlaFilterPreset } from "./types";
import { getTriggerSizeClasses, type GlaSelectorSize } from "./triggerSizes";
import { getVisibleAccounts } from "./utils";
import { cn } from "../../../lib/utils.ts";
import { fieldLabelClasses, fieldVariants } from "../../general/fieldStyles.ts";
import type { Subledger } from "../SubledgerSelector";
import { useSubledgerContext } from "../../../contexts/SubledgerContext.tsx";
import type {DocumentDirection} from "../../../generated/enums.ts";

export interface GLASelectorProps {
    accounts: GlAccount[];
    value: GlAccount | null;
    onChange: (account: GlAccount | null) => void;
    filterPreset?: GlaFilterPreset;
    documentDirectionFilter?: DocumentDirection;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    size?: GlaSelectorSize;
    selectedSubledger?: Subledger | null;
    onSubledgerChange?: (subledger: Subledger | null) => void;
}

export function GLASelector({
    accounts,
    value,
    onChange,
    filterPreset = GlaFilterPreset.ALL,
    documentDirectionFilter,
    placeholder = "Selecteer een rekening",
    label,
    disabled = false,
    size = "l",
    selectedSubledger,
    onSubledgerChange,
}: GLASelectorProps) {
    const { subledgers, createSubledger } = useSubledgerContext();
    const sizeClasses = getTriggerSizeClasses(size);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const focusSearchInput = useCallback(() => {
        searchInputRef.current?.focus();
    }, []);

    const hasActiveFilter =
        filterPreset !== GlaFilterPreset.ALL ||
        !!documentDirectionFilter;
    const [showingAll, setShowingAll] = useState(() => !hasActiveFilter);

    const visibleAccounts = useMemo(
        () =>
            getVisibleAccounts(
                accounts,
                filterPreset,
                documentDirectionFilter,
                showingAll
            ),
        [accounts, filterPreset, documentDirectionFilter, showingAll]
    );

    function handleSelect(account: GlAccount, close: () => void) {
        if (disabled) return;
        onChange(account);
        close();
    }

    async function handleCreateSubledger(name: string) {
        const created = await createSubledger(name);
        onSubledgerChange?.(created);
    }

    return (
        <Popover className="relative w-full">
            {({ open }) => (
                <>
                    {label && (
                        <label className={cn(fieldLabelClasses, "mb-1")}>{label}</label>
                    )}

                    {value ? (
                        <GLASelectorPill
                            account={value}
                            onClear={() => onChange(null)}
                            disabled={disabled}
                            sizeClasses={sizeClasses}
                            subledgerLabel={selectedSubledger?.name}
                        />
                    ) : (
                        <PopoverButton
                            disabled={disabled}
                            className={cn(
                                fieldVariants(),
                                "group relative flex items-center text-left",
                                sizeClasses.button,
                                disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                            )}
                        >
                            <GLASelectorTrigger placeholder={placeholder} sizeClasses={sizeClasses} />
                        </PopoverButton>
                    )}

                    {!disabled && (
                        <Transition
                            show={open}
                            afterEnter={focusSearchInput}
                            enter="transition duration-100 ease-out"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="transition duration-75 ease-in"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <PopoverPanel
                                static
                                anchor="bottom start"
                                className="z-50 mt-4 origin-top"
                            >
                                {({ close }) => (
                                    <GLASelectorMenu
                                        searchInputRef={searchInputRef}
                                        accounts={visibleAccounts}
                                        onSelect={(account) => handleSelect(account, close)}
                                        hasActiveFilter={hasActiveFilter}
                                        showingAll={showingAll}
                                        onToggleShowAll={() => setShowingAll((prev) => !prev)}
                                        subledgers={onSubledgerChange ? subledgers : undefined}
                                        selectedSubledger={selectedSubledger}
                                        onSubledgerChange={onSubledgerChange}
                                        onCreateSubledger={onSubledgerChange ? handleCreateSubledger : undefined}
                                    />
                                )}
                            </PopoverPanel>
                        </Transition>
                    )}
                </>
            )}
        </Popover>
    );
}
